import { makeStyles } from '@material-ui/styles';
import { ChannelPreviewUIComponentProps } from 'stream-chat-react';
import { format, differenceInDays } from 'date-fns';
import { useAtom } from 'jotai';
import cn from 'clsx';
import { useErrorHandler } from 'react-error-boundary';

import { useChatClient } from '@bizzabo/chat-core';
import { Avatar } from '@bizzabo/collaboration-common';
import { Text } from '@bizzabo/design-system';
import { fetchProfileIdByChatId } from '@bizzabo/entities';

import { useMessages, View } from '../../../contexts/MessagesContext';
import { extractChatPartner } from '../../../utils/helpers';
import useTypeTracking from '../../../../../analytics';
import { incomingCallAtom } from '../../../../../notifications/videoNotifications/atoms';

const formatMessageTime = (timeOfMessage: Date) => {
    if (!timeOfMessage) {
        return '';
    }
    const daysDiff = differenceInDays(new Date(), timeOfMessage);
    if (daysDiff > 1) {
        return `${daysDiff} days ago`;
    } else if (daysDiff === 1) {
        return 'Yesterday';
    } else {
        return format(timeOfMessage, "h:mm aaaaa'm'");
    }
};

const CustomChannelListItem: React.FC<ChannelPreviewUIComponentProps> = ({
    channel,
    setActiveChannel,
    watchers,
}) => {
    const classes = useStyles();
    const { trackClickEvent } = useTypeTracking();
    const { client } = useChatClient();
    const { eventId, setView } = useMessages();
    const [incomingCall] = useAtom(incomingCallAtom);
    const handleError = useErrorHandler();

    const partnerObj = extractChatPartner(channel, client);

    const { messages } = channel.state;
    const messageToDisplay =
        messages.length === 0 ? 'No messages yet' : messages[messages.length - 1].text;
    const timeOfMessage = messages.length === 0 ? null : messages[messages.length - 1].created_at;

    const { id, firstName, lastName } = partnerObj || { firstName: '', lastName: '' };
    const nameToDisplay = firstName || lastName ? firstName + ' ' + lastName : channel.data.name;
    const hasUnreadMessages = !!channel.state.unreadCount;
    const photoUrl = (partnerObj?.photoUrl as string) || '';

    const onListItemClick = async () => {
        setActiveChannel(channel, watchers);
        setView(View.Channel);
        try {
            const attendeeId = await fetchProfileIdByChatId(eventId, partnerObj?.id);
            trackClickEvent({
                subCategory: 'VIEWED_CONTACT',
                description: { attendee_id: attendeeId },
            });
        } catch (error) {
            handleError(error);
        }
    };

    const isPartnerCalling =
        incomingCall.hasIncomingCall &&
        incomingCall.incomingNotification.callerChatProviderUserId === id;

    return (
        <div
            className={classes.previewContainer}
            onClick={onListItemClick}
            aria-label="channel item"
        >
            <Avatar
                src={photoUrl}
                size={40}
                firstName={String(firstName)}
                lastName={String(lastName)}
                alt="user-image"
            />
            <div className={classes.nameAndMessage}>
                <Text className={classes.name} variant="t60">
                    {nameToDisplay}
                </Text>
                {isPartnerCalling ? (
                    <Text className={cn(classes.text, classes.red)} variant="t81">
                        Calling you...
                    </Text>
                ) : (
                    <Text className={cn(classes.text, classes.gray)} variant="t81">
                        {messageToDisplay}
                    </Text>
                )}
            </div>
            <div className={classes.timeAndNotification}>
                <Text className={classes.time} variant="t81">
                    {formatMessageTime(timeOfMessage)}
                </Text>
                {hasUnreadMessages && (
                    <Text className={classes.notification} variant="t81">
                        {channel.state.unreadCount}
                    </Text>
                )}
            </div>
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        previewContainer: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '17px 24px',
            borderTop: `1px solid ${theme.palette.greys[200]}`,
            cursor: 'pointer',
        },
        nameAndMessage: {
            display: 'flex',
            flexDirection: 'column',
            marginLeft: 8,
            flexGrow: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        timeAndNotification: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            width: 80,
            height: 36,
        },
        name: {
            color: theme.palette.greys[700],
        },
        time: {
            color: theme.palette.greys[500],
            width: 80,
            textAlign: 'right',
        },
        text: {
            lineHeight: 1.3,
        },
        red: {
            color: theme.palette.primary[500],
        },
        gray: {
            color: theme.palette.greys[500],
        },
        notification: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            background: theme.palette.notification,
            color: theme.palette.greys[100],
            borderRadius: 30,
            fontSize: '1rem',
            fontWeight: 500,
        },
    }),
    { classNamePrefix: CustomChannelListItem.name }
);

export default CustomChannelListItem;
